import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Blocks, Search, ListChecks } from 'lucide-react';
import logger from '@/lib/logger';

/**
 * Modal that lets the user pick one or more phase blocks and apply them
 * (sequentially) to the given project_id. On success calls `onApplied()`
 * so the parent can refresh.
 */
function PhaseBlocksPicker({ open, onOpenChange, projectId, onApplied }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/phase-blocks');
      setBlocks(res.data || []);
    } catch (e) {
      logger.error('Error fetching phase blocks:', e);
      toast.error('Erro ao carregar blocos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchBlocks();
      setSelected(new Set());
      setSearch('');
    }
  }, [open, fetchBlocks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return blocks;
    return blocks.filter((b) =>
      (b.name || '').toLowerCase().includes(q) ||
      (b.category || '').toLowerCase().includes(q)
    );
  }, [blocks, search]);

  const toggle = (blockId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  const handleApply = async () => {
    if (selected.size === 0) {
      toast.error('Selecione ao menos um bloco');
      return;
    }
    setApplying(true);
    let okCount = 0;
    let failCount = 0;
    try {
      // Apply sequentially to keep the "order" deterministic
      for (const blockId of selected) {
        try {
          await api.post(`/phase-blocks/${blockId}/apply/${projectId}`);
          okCount += 1;
        } catch (e) {
          failCount += 1;
          logger.error(`Failed to apply block ${blockId}:`, e);
        }
      }
      if (okCount > 0) {
        toast.success(`${okCount} bloco(s) aplicado(s) ao projeto`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} bloco(s) falharam`);
      }
      if (onApplied) onApplied();
      onOpenChange(false);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Blocks className="w-5 h-5 text-emerald-700" />
            Adicionar Blocos de Fase
          </DialogTitle>
          <DialogDescription>
            Selecione um ou mais blocos para inserir como fases neste projeto.
            Cada bloco selecionado cria uma fase + todas as suas tarefas.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 border rounded-md px-2 py-1 bg-gray-50">
          <Search className="w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar bloco por nome ou categoria…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 bg-transparent focus-visible:ring-0 shadow-none"
            data-testid="phase-blocks-picker-search"
          />
          <Badge variant="outline">{selected.size} selecionado(s)</Badge>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 -mx-1 px-1" data-testid="phase-blocks-picker-list">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-800"></div>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-12">
              {blocks.length === 0
                ? 'Nenhum bloco cadastrado. Acesse Templates → Blocos de Fase para criar ou importar.'
                : 'Nenhum bloco corresponde à busca.'}
            </p>
          ) : (
            filtered.map((b) => {
              const isSelected = selected.has(b.block_id);
              return (
                <Card
                  key={b.block_id}
                  className={`p-3 border cursor-pointer transition ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-emerald-300'
                  }`}
                  onClick={() => toggle(b.block_id)}
                  data-testid={`phase-block-picker-item-${b.block_id}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggle(b.block_id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 break-words">{b.name}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                        {b.category && <Badge variant="outline">{b.category}</Badge>}
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          Peso {b.weight}
                        </Badge>
                        <span className="text-gray-500 inline-flex items-center gap-1">
                          <ListChecks className="w-3.5 h-3.5" />
                          {(b.tasks || []).length} tarefa(s)
                        </span>
                      </div>
                      {b.description && (
                        <p className="text-xs text-gray-500 mt-1 break-words">{b.description}</p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            className="bg-emerald-700 hover:bg-emerald-800"
            onClick={handleApply}
            disabled={selected.size === 0 || applying}
            data-testid="phase-blocks-picker-apply"
          >
            {applying ? 'Aplicando…' : `Aplicar ${selected.size > 0 ? `(${selected.size})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PhaseBlocksPicker;
